import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Ruler, Calculator, Building, Compass } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './ArchitecturalSolutionsCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './ArchitecturalSolutionsCostCalculatorPage/components/ResultsOverview';
import ProjectBreakdown from './ArchitecturalSolutionsCostCalculatorPage/components/ProjectBreakdown';
import QuickEstimationSidebar from './ArchitecturalSolutionsCostCalculatorPage/components/QuickEstimationSidebar';
import SampleProjectsSidebar from './ArchitecturalSolutionsCostCalculatorPage/components/SampleProjectsSidebar';
import ArchitectureTipsSidebar from './ArchitecturalSolutionsCostCalculatorPage/components/ArchitectureTipsSidebar';
import InfoContent from './ArchitecturalSolutionsCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  projectType: 'residential',
  buildingArea: '150',
  numFloors: '2',
  complexity: 'medium',
  location: 'city',
  projectStage: 'concept',
  includeInteriorDesign: false,
  includeEngineeringSystems: true,
  includeConstructionDocumentation: true,
  includeProjectSupervision: false,
  includeVisualization: true,
  visualizationType: '3d_renders',
  numVisualizations: '3',
  revisionRounds: '2',
  urgency: 'normal',
  additionalRequirements: '',
  clientType: 'individual',
  currencyType: 'RUB'
};

// Определение типов проектов
export const projectTypes = [
  { id: 'residential', name: 'Жилой дом', defaultRatePerSqm: 1200 },
  { id: 'apartment', name: 'Квартира', defaultRatePerSqm: 1400 },
  { id: 'commercial', name: 'Коммерческое помещение', defaultRatePerSqm: 1800 },
  { id: 'office', name: 'Офисное помещение', defaultRatePerSqm: 1600 },
  { id: 'industrial', name: 'Промышленное здание', defaultRatePerSqm: 1100 },
  { id: 'public', name: 'Общественное здание', defaultRatePerSqm: 2200 },
  { id: 'landscape', name: 'Ландшафтный дизайн', defaultRatePerSqm: 900 }
];

// Определение уровней сложности
export const complexityLevels = [
  { id: 'low', name: 'Простой', factor: 0.8 },
  { id: 'medium', name: 'Средний', factor: 1.0 },
  { id: 'high', name: 'Сложный', factor: 1.3 },
  { id: 'very_high', name: 'Очень сложный', factor: 1.7 }
];

// Определение этапов проектирования
export const projectStages = [
  { id: 'concept', name: 'Концепция и эскизный проект', percentage: 20 },
  { id: 'design', name: 'Проектная документация', percentage: 40 },
  { id: 'working', name: 'Рабочая документация', percentage: 70 },
  { id: 'full', name: 'Полный пакет документации', percentage: 100 }
];

// Определение типов визуализации
export const visualizationTypes = [
  { id: '3d_renders', name: '3D рендеры', costPerUnit: 5000 },
  { id: 'animation', name: '3D анимация', costPerUnit: 25000 },
  { id: 'vr', name: 'VR прогулка', costPerUnit: 40000 },
  { id: 'sketches', name: 'Скетчи от руки', costPerUnit: 3000 }
];

// Определение типов локаций
export const locationTypes = [
  { id: 'city', name: 'Городская среда', factor: 1.0 },
  { id: 'suburban', name: 'Пригород', factor: 0.9 },
  { id: 'rural', name: 'Сельская местность', factor: 0.8 },
  { id: 'complex', name: 'Сложный ландшафт', factor: 1.2 },
  { id: 'waterfront', name: 'Прибрежная зона', factor: 1.3 }
];

// Определение типов срочности
export const urgencyTypes = [
  { id: 'low', name: 'Стандартные сроки', factor: 1.0 },
  { id: 'normal', name: 'Умеренно срочно', factor: 1.2 },
  { id: 'high', name: 'Срочно', factor: 1.5 },
  { id: 'very_high', name: 'Экстренно', factor: 1.8 }
];

/**
 * ArchitecturalSolutionsCostCalculatorPage Component - Полный калькулятор стоимости архитектурных решений
 */
const ArchitecturalSolutionsCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('architectural-solutions-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости архитектурных решений | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет стоимости архитектурного проектирования для различных типов зданий с учетом площади, сложности, локации, срочности и дополнительных услуг.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных проектов из localStorage ---
  useEffect(() => {
    const savedProjectsData = localStorage.getItem('savedArchProjects');
    if (savedProjectsData) {
      try {
        setSavedProjects(JSON.parse(savedProjectsData));
      } catch (e) {
        console.error("Error loading saved projects:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'residential':
          newState.projectType = 'residential';
          break;
        case 'commercial':
          newState.projectType = 'commercial';
          break;
        case 'interior':
          newState.includeInteriorDesign = true;
          break;
        case 'concept':
          newState.projectStage = 'concept';
          break;
        case 'full':
          newState.projectStage = 'full';
          break;
        case 'visualization':
          newState.includeVisualization = true;
          newState.visualizationType = '3d_renders';
          newState.numVisualizations = '5';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setBreakdown([]);
    setErrors({});
    setShowDetailedBreakdown(false);

  }, [subToolId]);

  // Сохранение проекта в localStorage
  const saveProject = (name) => {
    if (!results) return;

    const newSavedProject = {
      id: Date.now().toString(),
      name: name || `Проект ${formData.buildingArea}м² (${getProjectTypeById(formData.projectType)?.name})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProjects = [...savedProjects, newSavedProject];
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedArchProjects', JSON.stringify(updatedSavedProjects));

    return newSavedProject;
  };

  // Удаление сохраненного проекта
  const deleteSavedProject = (id) => {
    const updatedSavedProjects = savedProjects.filter(project => project.id !== id);
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedArchProjects', JSON.stringify(updatedSavedProjects));
  };

  // Загрузка сохраненного проекта
  const loadSavedProject = (id) => {
    const projectToLoad = savedProjects.find(project => project.id === id);
    if (projectToLoad) {
      setFormData(projectToLoad.formData);
      setResults(projectToLoad.results);
      // Пересчитаем расчет для визуализации
      if (projectToLoad.results.breakdownItems) {
        setBreakdown(projectToLoad.results.breakdownItems);
      }
    }
  };

  // Вспомогательная функция для получения типа проекта по ID
  const getProjectTypeById = (id) => {
    return projectTypes.find(type => type.id === id);
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Compass className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости архитектурных решений'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              breakdown={breakdown}
              setBreakdown={setBreakdown}
              projectTypes={projectTypes}
              complexityLevels={complexityLevels}
              projectStages={projectStages}
              visualizationTypes={visualizationTypes}
              locationTypes={locationTypes}
              urgencyTypes={urgencyTypes}
              saveProject={saveProject}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <ProjectBreakdown 
                  breakdown={breakdown} 
                  results={results}
                  showDetailedBreakdown={showDetailedBreakdown}
                  setShowDetailedBreakdown={setShowDetailedBreakdown}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент об архитектурном проектировании --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой оценки --- */}
            <QuickEstimationSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              projectTypes={projectTypes}
              complexityLevels={complexityLevels}
              projectStages={projectStages}
            />

            {/* --- Панель примеров проектов --- */}
            <SampleProjectsSidebar 
              savedProjects={savedProjects}
              deleteSavedProject={deleteSavedProject}
              loadSavedProject={loadSavedProject}
            />

            {/* --- Панель полезных советов --- */}
            <ArchitectureTipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Project Breakdown Table */
        .project-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .project-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .project-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .project-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .project-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .project-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .project-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .project-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .project-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default ArchitecturalSolutionsCostCalculatorPage;